// Label display mapping for defense industry labels
export const LABEL_DISPLAY_MAP: Record<
  string,
  { full: string; abbrev: string }
> = {
  defense_raw_materials: { full: "Defense Raw Materials", abbrev: "Raw Mat" },
  defense_chemical_processing: {
    full: "Defense Chemical Processing",
    abbrev: "Chem Proc",
  },
  armor_and_composite_materials: {
    full: "Armor & Composite Materials",
    abbrev: "Armor",
  },
  ordnance_and_munitions_manufacturing: {
    full: "Ordnance & Munitions Manufacturing",
    abbrev: "Ordnance",
  },
  weapon_systems_manufacturing: {
    full: "Weapon Systems Manufacturing",
    abbrev: "Weapons",
  },
  vehicle_platform_manufacturing: {
    full: "Vehicle Platform Manufacturing",
    abbrev: "Vehicles",
  },
  uav_platform_manufacturing: {
    full: "UAV Platform Manufacturing",
    abbrev: "UAV Plat",
  },
  uav_payload_manufacturing: {
    full: "UAV Payload Manufacturing",
    abbrev: "UAV Pay",
  },
  uav_system_integration: {
    full: "UAV System Integration",
    abbrev: "UAV Sys",
  },
  uav_command_and_control_systems: {
    full: "UAV Command & Control Systems",
    abbrev: "UAV C2",
  },
  uav_autonomy_and_ai_development: {
    full: "UAV Autonomy & AI Development",
    abbrev: "UAV AI",
  },
  aerospace_and_defense_electronics: {
    full: "Aerospace & Defense Electronics",
    abbrev: "A&D Elec",
  },
  command_control_and_communications: {
    full: "Command, Control & Communications",
    abbrev: "C3",
  },
  cybersecurity_and_electronic_warfare: {
    full: "Cybersecurity & Electronic Warfare",
    abbrev: "Cyber/EW",
  },
  defense_software_and_ai_systems: {
    full: "Defense Software & AI Systems",
    abbrev: "SW/AI",
  },
  surveillance_and_reconnaissance_systems: {
    full: "Surveillance & Reconnaissance Systems",
    abbrev: "ISR",
  },
  space_defense_and_satellite_systems: {
    full: "Space Defense & Satellite Systems",
    abbrev: "Space",
  },
  defense_system_integration: {
    full: "Defense System Integration",
    abbrev: "Integration",
  },
  maintenance_repair_and_overhaul: {
    full: "Maintenance, Repair & Overhaul",
    abbrev: "MRO",
  },
  training_and_simulation: {
    full: "Training & Simulation",
    abbrev: "Training",
  },
  logistics_and_supply_chain_support: {
    full: "Logistics & Supply Chain Support",
    abbrev: "Logistics",
  },
  private_military_and_security_services: {
    full: "Private Military & Security Services",
    abbrev: "PMSC",
  },
  dual_use_and_defense_conversion: {
    full: "Dual Use & Defense Conversion",
    abbrev: "Dual Use",
  },
  anti_drone_systems: {
    full: "Anti Drone Systems",
    abbrev: "Anti Drone",
  },
};

/**
 * Get display information for a defense industry label
 * @param label - The snake_case label key
 * @returns Object with full name and abbreviation
 */
export const getLabelDisplay = (label: string) => {
  return (
    LABEL_DISPLAY_MAP[label] || {
      full: label.replace(/_/g, " "),
      abbrev: label,
    }
  );
};

/**
 * Get full display name for a label
 * @param label - The snake_case label key
 * @returns Full descriptive name
 */
export const getLabelFullName = (label: string) => {
  return getLabelDisplay(label).full;
};

/**
 * Get abbreviation for a label
 * @param label - The snake_case label key
 * @returns Short abbreviation
 */
export const getLabelAbbreviation = (label: string) => {
  return getLabelDisplay(label).abbrev;
};

/**
 * Get label key from full display name
 * @param fullName - The full display name
 * @returns The snake_case label key
 */
export const getLabelKeyFromFullName = (fullName: string): string => {
  return (
    Object.keys(LABEL_DISPLAY_MAP).find(
      (key) => getLabelFullName(key) === fullName
    ) || fullName
  );
};

/**
 * Convert array of label keys to full display names
 * @param keys - Array of snake_case label keys
 * @returns Array of full display names
 */
export const getFullNamesFromKeys = (keys: string[]): string[] => {
  return keys.map((key) => getLabelFullName(key));
};

/**
 * Convert array of full display names to label keys
 * @param fullNames - Array of full display names
 * @returns Array of snake_case label keys
 */
export const getKeysFromFullNames = (fullNames: string[]): string[] => {
  return fullNames.map(getLabelKeyFromFullName);
};
